import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  getCrmLeadDetail,
  getCrmLeadTimeline,
  downloadCrmLeadComments,
  completeCrmVideoCallRequest,
  updateCrmLead,
  updateCrmLeadTags,
  setCrmLeadVariable,
  removeCrmLeadHubLink,
} from '../config/api';
import CrmCallDialog from '../components/CrmCallDialog';
import CrmCommentDialog from '../components/CrmCommentDialog';
import CrmAddContactDialog from '../components/CrmAddContactDialog';
import CrmLinkIdDialog from '../components/CrmLinkIdDialog';
import CrmMergeLeadsDialog from '../components/CrmMergeLeadsDialog';
import CrmSplitLeadDialog from '../components/CrmSplitLeadDialog';
import CrmUnlinkIdDialog from '../components/CrmUnlinkIdDialog';
import CrmAssignLeadDialog from '../components/CrmAssignLeadDialog';
import CrmEditContactDialog from '../components/CrmEditContactDialog';
import CrmAddHubDialog from '../components/CrmAddHubDialog';
import CrmWhatsappConversations from '../components/CrmWhatsappConversations';
import formatCallDuration from '../utils/formatCallDuration';
import CrmArchiveLeadDialog from '../components/CrmArchiveLeadDialog';
import CrmDeleteLeadDialog from '../components/CrmDeleteLeadDialog';
import CrmStageDialog from '../components/CrmStageDialog';
import useIsMobile from '../hooks/useIsMobile';
import CrmDeviceDialDialog from '../components/CrmDeviceDialDialog';
import { canUseDeviceDial } from '../utils/crmPhone';
import { hasCrmAdminAccess } from '../utils/crmAccess';

const STAGE_LABELS = {
  not_contacted: 'Not contacted',
  contacted: 'Contacted',
  interested: 'Interested',
  app_registered: 'App registered',
  active: 'Active',
  inactive: 'Inactive',
};

const EXTERNAL_ID_LABELS = {
  pmsg_vendor_id: 'PMSG Vendor ID',
  oms_user_id: 'Account',
  gst: 'GST',
};

const externalIdDisplay = (externalId) => {
  if (externalId.type !== 'oms_user_id') return externalId.value;
  if (!externalId.omsAccount) return `Unavailable account (#${externalId.value})`;

  const { name, phone } = externalId.omsAccount;
  return [name || `Customer #${externalId.value}`, phone].filter(Boolean).join(' · ');
};

const TIMELINE_TABS = [
  { key: 'all', label: 'All' },
  { key: 'call', label: 'Calls' },
  { key: 'comment', label: 'Comments' },
  { key: 'whatsapp', label: 'WhatsApp' },
  { key: 'order', label: 'Orders' },
  { key: 'system', label: 'System' },
];

const money = (v) =>
  `₹${Number(v || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
const count = (value) => Number(value || 0).toLocaleString('en-IN');
const capacity = (value) =>
  `${Number(value || 0).toLocaleString('en-IN', { maximumFractionDigits: 3 })} kW`;

// Tooltip for a Call / WhatsApp action, including the mobile dialer fallback.
const contactActionTitle = (contact, action, allowDeviceDial = false) => {
  const allowed = action === 'call' ? contact.callable : contact.messageable;
  if (allowed) return action === 'call' ? 'Call this contact' : 'Message this contact';
  if (action === 'call' && allowDeviceDial && canUseDeviceDial(contact)) {
    return 'Call using your phone dialer';
  }
  if (action === 'whatsapp') {
    return 'This contact is marked no-WhatsApp';
  }
  if (!contact.registered) {
    return 'Not registered in the app — CRM calling unavailable';
  }
  return 'This contact is marked do-not-call';
};
// Phones have no hover tooltip: the distinct reasons a contact's Call /
// WhatsApp buttons are disabled, shown as visible text on mobile.
const contactBlockedReasons = (contact, actions = ['call', 'whatsapp'], allowDeviceDial = false) =>
  Array.from(new Set(
    actions
      .filter((action) => !(
        action === 'call'
          ? contact.callable || (allowDeviceDial && canUseDeviceDial(contact))
          : contact.messageable
      ))
      .map((action) => contactActionTitle(contact, action, allowDeviceDial))
  ));
const CALL_STATUS = {
  initiated: { label: 'Initiated', className: 'status-pending' },
  answered_by_agent: { label: 'Agent answered', className: 'status-pending' },
  answered_by_customer: { label: 'Customer answered', className: 'status-pending' },
  answered: { label: 'Connected', className: 'status-confirmed' },
  connected: { label: 'Connected', className: 'status-confirmed' },
  no_answer: { label: 'No answer', className: 'crm-call-status-unsuccessful' },
  missed: { label: 'Missed', className: 'crm-call-status-unsuccessful' },
};

const CrmLeadDetail = () => {
  const { leadId } = useParams();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const moreActionsRef = useRef(null);
  const [lead, setLead] = useState(null);
  const [loading, setLoading] = useState(true);
  const [timelineTab, setTimelineTab] = useState('all');
  const [timelineEvents, setTimelineEvents] = useState([]);
  const [timelinePage, setTimelinePage] = useState(1);
  const [timelinePagination, setTimelinePagination] = useState({ total: 0, page: 1, limit: 20, totalPages: 0 });
  const [timelineLoading, setTimelineLoading] = useState(true);
  const [timelineError, setTimelineError] = useState('');
  const [timelineRevision, setTimelineRevision] = useState(0);
  // When set, the WhatsApp tab auto-opens this contact's thread (used when a
  // WhatsApp row in the mixed timeline is clicked).
  const [openWhatsappContactId, setOpenWhatsappContactId] = useState(null);
  // Calls tab: filter the call list by a specific contact ('' = all).
  const [callContactFilter, setCallContactFilter] = useState('');
  const [callContact, setCallContact] = useState(null);
  const [dialContact, setDialContact] = useState(null);
  const [commentDialogOpen, setCommentDialogOpen] = useState(false);
  const [exportingComments, setExportingComments] = useState(false);
  const [completingVideoCallId, setCompletingVideoCallId] = useState(null);
  const [videoCallError, setVideoCallError] = useState('');
  const [addContactDialogOpen, setAddContactDialogOpen] = useState(false);
  const [linkIdDialogOpen, setLinkIdDialogOpen] = useState(false);
  const [mergeDialogOpen, setMergeDialogOpen] = useState(false);
  const [splitDialogOpen, setSplitDialogOpen] = useState(false);
  const [unlinkExternalId, setUnlinkExternalId] = useState(null);
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [editContact, setEditContact] = useState(null);
  const [addHubDialogOpen, setAddHubDialogOpen] = useState(false);
  const [archiveAction, setArchiveAction] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [stageDialogOpen, setStageDialogOpen] = useState(false);
  const [editingLeadName, setEditingLeadName] = useState(false);
  const [leadNameDraft, setLeadNameDraft] = useState('');
  const [savingLeadName, setSavingLeadName] = useState(false);
  const [leadNameError, setLeadNameError] = useState('');
  const [showAllContacts, setShowAllContacts] = useState(false);

  // Tags editing
  const [newTag, setNewTag] = useState('');
  const [savingTags, setSavingTags] = useState(false);

  // Variable editing (per-lead)
  const [editingVariableId, setEditingVariableId] = useState(null);
  const [variableDraft, setVariableDraft] = useState('');
  const [savingVariable, setSavingVariable] = useState(false);
  const loadedLeadId = lead?.id;

  const closeMoreActions = () => {
    if (moreActionsRef.current) moreActionsRef.current.open = false;
  };

  const canCallContact = (contact) => Boolean(
    contact?.callable || (isMobile && canUseDeviceDial(contact))
  );

  const handleCallContact = (contact) => {
    if (contact?.callable) {
      setCallContact(contact);
      return;
    }
    if (isMobile && canUseDeviceDial(contact)) setDialContact(contact);
  };

  useEffect(() => {
    setTimelineEvents([]);
    setTimelinePage(1);
    setTimelinePagination({ total: 0, page: 1, limit: 20, totalPages: 0 });
    setShowAllContacts(false);
    loadLead();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leadId]);

  useEffect(() => {
    if (!loadedLeadId || String(loadedLeadId) !== String(leadId)) return undefined;
    let cancelled = false;
    const loadTimeline = async () => {
      setTimelineLoading(true);
      setTimelineError('');
      try {
        const response = await getCrmLeadTimeline(leadId, {
          type: timelineTab,
          page: timelinePage,
          limit: 20,
        });
        const result = response?.data || response;
        if (cancelled) return;
        const nextEvents = result?.events || [];
        setTimelineEvents((current) => {
          if (timelinePage === 1) return nextEvents;
          const existingIds = new Set(current.map((event) => event.id));
          return [...current, ...nextEvents.filter((event) => !existingIds.has(event.id))];
        });
        setTimelinePagination(result?.pagination || { total: 0, page: 1, limit: 20, totalPages: 0 });
      } catch (err) {
        if (cancelled) return;
        if (timelinePage === 1) setTimelineEvents([]);
        setTimelineError(err.message || 'Could not load the timeline.');
      } finally {
        if (!cancelled) setTimelineLoading(false);
      }
    };
    loadTimeline();
    return () => { cancelled = true; };
  }, [leadId, loadedLeadId, timelineTab, timelinePage, timelineRevision]);

  const loadLead = async () => {
    setLoading(true);
    try {
      const res = await getCrmLeadDetail(leadId);
      const detail = res?.data || res;
      setLead(detail);
      setTimelinePage(1);
      setTimelineRevision((revision) => revision + 1);
      setLeadNameDraft(detail?.displayName || '');
    } catch (err) {
      console.error('Error loading lead:', err);
      alert(err.message || 'Failed to load lead');
    } finally {
      setLoading(false);
    }
  };

  const exportComments = async () => {
    if (exportingComments) return;
    setExportingComments(true);
    try {
      await downloadCrmLeadComments(leadId);
    } catch (err) {
      alert(err.message || 'Could not export Lead comments.');
    } finally {
      setExportingComments(false);
    }
  };

  const completeVideoCallRequest = async (requestId) => {
    if (!requestId || completingVideoCallId) return;
    setCompletingVideoCallId(requestId);
    setVideoCallError('');
    try {
      const response = await completeCrmVideoCallRequest(leadId, requestId);
      const updated = response?.data || response;
      setLead((current) => ({
        ...current,
        videoCallRequests: (current.videoCallRequests || []).map((request) => (
          request.requestId === requestId ? { ...request, ...updated } : request
        )),
      }));
      setTimelinePage(1);
      setTimelineRevision((revision) => revision + 1);
    } catch (err) {
      setVideoCallError(err.message || 'Could not mark the video call completed.');
    } finally {
      setCompletingVideoCallId(null);
    }
  };

  const saveLeadName = async () => {
    const displayName = leadNameDraft.trim();
    if (savingLeadName) return;
    if (!displayName) {
      setLeadNameError('Lead name is required.');
      return;
    }
    if (displayName === (lead.displayName || '')) {
      setEditingLeadName(false);
      setLeadNameError('');
      return;
    }

    setSavingLeadName(true);
    setLeadNameError('');
    try {
      await updateCrmLead(lead.id, { displayName });
      setLead((current) => ({ ...current, displayName }));
      setTimelineRevision((revision) => revision + 1);
      setEditingLeadName(false);
    } catch (err) {
      setLeadNameError(err.message || 'Could not update the Lead name.');
    } finally {
      setSavingLeadName(false);
    }
  };

  const applyTagsResult = (res) => {
    const tags = (res?.data && res.data.tags) || res?.tags;
    if (Array.isArray(tags)) {
      setLead((prev) => (prev ? { ...prev, tags } : prev));
    }
  };

  const handleAddTag = async () => {
    const tag = newTag.trim();
    if (!tag || savingTags) return;
    setSavingTags(true);
    try {
      const res = await updateCrmLeadTags(leadId, { add: [tag] });
      applyTagsResult(res);
      setNewTag('');
      setTimelineRevision((revision) => revision + 1);
    } catch (err) {
      alert(err.message || 'Could not add tag.');
    } finally {
      setSavingTags(false);
    }
  };

  const startEditVariable = (variable) => {
    setEditingVariableId(variable.id);
    setVariableDraft(variable.value == null ? '' : String(variable.value));
  };

  const cancelEditVariable = () => {
    setEditingVariableId(null);
    setVariableDraft('');
  };

  const handleSaveVariable = async (variable) => {
    if (savingVariable) return;
    setSavingVariable(true);
    try {
      await setCrmLeadVariable(leadId, variable.id, variableDraft);
      setEditingVariableId(null);
      setVariableDraft('');
      await loadLead();
    } catch (err) {
      alert(err.message || 'Could not save the variable value.');
    } finally {
      setSavingVariable(false);
    }
  };

  const handleRemoveTag = async (tag) => {
    if (savingTags) return;
    setSavingTags(true);
    try {
      const res = await updateCrmLeadTags(leadId, { remove: [tag] });
      applyTagsResult(res);
      setTimelineRevision((revision) => revision + 1);
    } catch (err) {
      alert(err.message || 'Could not remove tag.');
    } finally {
      setSavingTags(false);
    }
  };

  const [removingHubId, setRemovingHubId] = useState(null);
  const handleRemoveHub = async (link) => {
    if (removingHubId) return;
    const label = [link.name, link.city].filter(Boolean).join(' · ') || `Hub #${link.hubId}`;
    if (!window.confirm(`Remove ${label} from this lead?`)) return;
    setRemovingHubId(link.hubId);
    try {
      await removeCrmLeadHubLink(lead.id, link.hubId);
      await loadLead();
    } catch (err) {
      alert(err.message || 'Could not remove the hub.');
    } finally {
      setRemovingHubId(null);
    }
  };

  const formatDateTime = (d) => (
    d
      ? new Date(d).toLocaleString('en-IN', {
          timeZone: 'Asia/Kolkata',
          day: 'numeric',
          month: 'short',
          year: 'numeric',
          hour: 'numeric',
          minute: '2-digit',
        })
      : '—'
  );
  const formatFollowUpValue = (value) => {
    const text = String(value || '');
    const dateOnly = text.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (dateOnly) {
      return new Intl.DateTimeFormat('en-IN', {
        timeZone: 'UTC',
        year: 'numeric',
        month: 'short',
        day: '2-digit',
      }).format(new Date(Date.UTC(
        Number(dateOnly[1]),
        Number(dateOnly[2]) - 1,
        Number(dateOnly[3])
      )));
    }
    const date = new Date(text);
    return Number.isNaN(date.getTime())
      ? text
      : date.toLocaleString('en-IN', {
          timeZone: 'Asia/Kolkata',
          dateStyle: 'medium',
          timeStyle: 'short',
        });
  };

  // Present a 10-digit number as +91 XXXXX XXXXX; pass through anything else.
  const formatPhone = (value) => {
    const digits = String(value || '').replace(/\D/g, '').slice(-10);
    if (digits.length !== 10) return value || '';
    return `+91 ${digits.slice(0, 5)} ${digits.slice(5)}`;
  };
  const formatDate = (d) => (
    d
      ? new Date(d).toLocaleDateString('en-IN', {
          day: 'numeric',
          month: 'short',
          year: 'numeric',
        })
      : '—'
  );

  if (loading) {
    return (
      <div className="container">
        <div className="card" style={{ padding: '24px' }}>Loading…</div>
      </div>
    );
  }

  if (!lead) {
    return (
      <div className="container">
        <div className="card" style={{ padding: '24px' }}>
          Lead not found. <Link to="/crm/leads">Back to leads</Link>
        </div>
      </div>
    );
  }

  const orders = lead.orders || {};
  const pmsg = lead.pmsg || {};
  const contacts = lead.contacts || [];
  const orderedContacts = [...contacts].sort((a, b) => Number(b.isPrimary) - Number(a.isPrimary));
  const visibleContacts = showAllContacts ? orderedContacts : orderedContacts.slice(0, 5);
  const primaryContact = contacts.find((contact) => contact.isPrimary) || contacts[0];
  const externalIds = lead.externalIds || [];
  const hubLinks = lead.hubLinks || [];
  const callEvents = lead.recentCalls || [];
  const videoCallRequests = lead.videoCallRequests || [];
  const pendingVideoCallRequests = videoCallRequests.filter(
    (request) => request.status === 'requested'
  );

  const stageBadgeClass =
    lead.stage === 'active'
      ? 'status-badge status-confirmed'
      : lead.stage === 'inactive'
      ? 'status-badge status-pending'
      : 'status-badge';

  const tlIconClass = (type) => {
    if (type === 'call') return 'call';
    if (type === 'whatsapp') return 'whatsapp';
    if (type === 'order') return 'order';
    if (type === 'notification') return 'notification';
    if (type === 'comment') return 'comment';
    return 'system';
  };
  const tlIconLetter = (type) => {
    if (type === 'call') return 'C';
    if (type === 'whatsapp') return 'W';
    if (type === 'order') return 'O';
    if (type === 'notification') return 'N';
    if (type === 'comment') return '✎';
    if (type === 'merge') return 'M';
    return 'S';
  };

  const renderTimelineBody = (event) => {
    const d = event.data || {};
    if (event.type === 'call') {
      const callStatus = CALL_STATUS[d.providerStatus];
      return (
        <div>
          <div className="crm-tl-title">
            <strong>Call</strong>
            {callStatus && <span className={`status-badge crm-call-status ${callStatus.className}`}>{callStatus.label}</span>}
            {['answered', 'connected'].includes(d.providerStatus) && d.duration > 0
              ? <span className="crm-tl-detail">{formatCallDuration(d.duration, d.durationSource === 'estimated')}</span>
              : null}
            {d.dispositionLabel && <span className="crm-tl-detail">{d.dispositionLabel}</span>}
            {d.isQuickLog && <span className="crm-tl-quiet-chip">Quick log</span>}
          </div>
          {d.comment && <div className="crm-tl-text">{d.comment}</div>}
        </div>
      );
    }
    if (event.type === 'whatsapp') {
      const isInbound = d.direction === 'inbound';
      const channelLabel =
        d.channel === 'group' ? 'Group' : d.channel === 'direct' ? 'Direct' : null;
      // WhatsApp-style thread: inbound bubble left, outbound bubble right.
      const senderLabel = isInbound
        ? (d.contactName || 'Customer')
        : (d.user || 'Agent');
      // Footer meta shown subtly under the bubble text.
      const footerBits = [];
      if (channelLabel) footerBits.push(channelLabel);
      if (d.hubNumber) footerBits.push(d.hubNumber);
      if (!isInbound && d.user) footerBits.push(d.user);
      if (d.orderId && !String(d.orderId).startsWith('MANUAL-')) footerBits.push(`Order ${d.orderId}`);

      return (
        <div className={`crm-chat-row crm-chat-row-${isInbound ? 'in' : 'out'}`}>
          <div className={`crm-chat-bubble crm-chat-bubble-${isInbound ? 'in' : 'out'}`}>
            <div className="crm-chat-sender">
              <span className="crm-chat-sender-name">{senderLabel}</span>
              {d.contactPhone && (
                <span className="crm-chat-sender-phone">{formatPhone(d.contactPhone)}</span>
              )}
            </div>

            <div className={`crm-chat-text${d.text ? '' : ' crm-chat-text-empty'}`}>
              {d.text || 'Message content was not stored for this older event.'}
            </div>

            {footerBits.length > 0 && (
              <div className="crm-chat-footer-meta">{footerBits.join(' · ')}</div>
            )}

            <div className="crm-chat-time">
              <span>{formatDateTime(event.timestamp)}</span>
              {!isInbound && d.status && (
                <span className={`crm-chat-tick crm-chat-tick-${d.status}`}>
                  {d.status === 'sent' ? '✓✓' : d.status === 'failed' ? '⚠' : ''}
                </span>
              )}
            </div>
          </div>
        </div>
      );
    }
    if (event.type === 'order') {
      return (
        <div>
          <div className="crm-tl-title">
            <strong>Order {d.orderId || ''}</strong>
            {d.status ? ` · ${d.status}` : ''}
            {d.value != null ? ` · ${money(d.value)}` : ''}
          </div>
        </div>
      );
    }
    if (event.type === 'comment') {
      const rawText = String(d.text || '');
      const callFeedbackMatch = rawText.match(/^\[\[crm:call-feedback:(\d+)\]\]\s*/i);
      const isCallFeedback = d.isCallFeedback || Boolean(callFeedbackMatch);
      const displayText = callFeedbackMatch
        ? rawText.slice(callFeedbackMatch[0].length)
        : rawText;
      return (
        <div>
          <div className="crm-tl-title">
            <strong>{isCallFeedback ? 'Call feedback' : 'Comment'}</strong>
          </div>
          <div className="crm-tl-text">{displayText}</div>
        </div>
      );
    }
    if (event.type === 'edit') {
      if (d.entity === 'hub_link' && d.field === 'hub_id') {
        const hubName = d.newValue || d.oldValue || 'Hub';
        const isRemoval = d.newValue == null || String(d.newValue).trim() === '';
        return (
          <div>
            <div className="crm-tl-title"><strong>{d.headline || (isRemoval ? 'Hub removed' : 'Hub linked')}</strong></div>
            <div className="crm-tl-hub-change">
              <span className="crm-tl-hub-icon" aria-hidden="true">H</span>
              <div>
                <div className="crm-tl-hub-label">Hub</div>
                <strong>{hubName}</strong>
              </div>
            </div>
          </div>
        );
      }

      if (d.field === 'split') {
        const parseSelection = (raw) => {
          try {
            const value = JSON.parse(String(raw || ''));
            if (value && typeof value === 'object') {
              return {
                contacts: Array.isArray(value.contacts) ? value.contacts.length : 0,
                externalIds: Array.isArray(value.externalIds) ? value.externalIds.length : 0,
              };
            }
          } catch (_) { /* older split records may contain plain text */ }
          return null;
        };
        const selection = parseSelection(d.oldValue) || parseSelection(d.newValue);
        const sourceMatch = String(d.oldValue || '').match(/^Created from Lead #(\d+)$/i);
        const destinationMatch = String(d.newValue || '').match(/^Moved to Lead #(\d+)$/i);
        const linkedLeadId = sourceMatch?.[1] || destinationMatch?.[1];
        const direction = sourceMatch ? 'Created from' : 'Moved records to';
        const selectionParts = selection
          ? [
              `${selection.contacts} contact${selection.contacts === 1 ? '' : 's'}`,
              `${selection.externalIds} linked identifier${selection.externalIds === 1 ? '' : 's'}`,
            ]
          : [];

        return (
          <div className="crm-tl-split">
            <div className="crm-tl-split-heading">
              <strong>Lead split</strong>
              <span>{sourceMatch ? 'New Lead' : 'Source Lead'}</span>
            </div>
            <div className="crm-tl-split-detail">
              <span className="crm-tl-split-icon" aria-hidden="true">↗</span>
              <div>
                <div>
                  {direction}{' '}
                  {linkedLeadId ? (
                    <Link to={`/crm/leads/${linkedLeadId}`}>Lead #{linkedLeadId}</Link>
                  ) : 'another Lead'}
                </div>
                {selectionParts.length > 0 && (
                  <div className="crm-tl-split-summary">{selectionParts.join(' · ')}</div>
                )}
              </div>
            </div>
          </div>
        );
      }

      // Try to parse a JSON value and render it as a human-readable summary.
      // Contact created/deleted values are objects with name, phone, etc.
      const formatValue = (raw) => {
        if (raw == null || raw === '') return '—';
        const str = String(raw);
        if (d.field === 'follow_up_date') return formatFollowUpValue(str);
        try {
          const obj = JSON.parse(str);
          if (obj && typeof obj === 'object' && !Array.isArray(obj)) {
            const parts = [];
            if (obj.name) parts.push(obj.name);
            if (obj.phone) parts.push(obj.phone);
            if (obj.designation) parts.push(obj.designation);
            if (parts.length) return parts.join(' · ');
          }
          // Array (e.g. tags) — join nicely
          if (Array.isArray(obj)) return obj.length ? obj.join(', ') : '(none)';
        } catch (_) { /* not JSON, display as-is */ }
        return str;
      };
      // Only show the changed-from / changed-to rows when they carry real info.
      const hasOld = d.oldValue != null && d.oldValue !== '';
      const hasNew = d.newValue != null && d.newValue !== '';
      return (
        <div>
          <div className="crm-tl-title"><strong>{d.headline || 'Edit'}</strong></div>
          {(hasOld || hasNew) && (
            <div className="crm-tl-change">
              {hasOld && (
                <div>
                  <span>From</span>
                  <strong>{formatValue(d.oldValue)}</strong>
                </div>
              )}
              {hasNew && (
                <div>
                  <span>To</span>
                  <strong>{formatValue(d.newValue)}</strong>
                </div>
              )}
            </div>
          )}
        </div>
      );
    }
    // notification / system / edit
    return (
      <div>
        <div className="crm-tl-title">
          <strong>{d.headline || 'System'}</strong>
          {d.subtype ? ` · ${d.subtype}` : ''}
        </div>
        {d.body && <div className="crm-tl-text">{d.body}</div>}
      </div>
    );
  };

  const userData = JSON.parse(localStorage.getItem('userData_30jan') || '{}');
  const canManageLeads = hasCrmAdminAccess(userData);
  const canArchiveLeads = hasCrmAdminAccess(userData);
  const canCompleteVideoCalls = canManageLeads
    || Number(lead.assignedUser?.id) === Number(userData.id);

  const handleMerged = (result) => {
    setMergeDialogOpen(false);
    if (String(result.survivorLeadId) === String(lead.id)) {
      loadLead();
      return;
    }
    navigate(`/crm/leads/${result.survivorLeadId}`, { replace: true });
  };

  const handleSplit = (result) => {
    setSplitDialogOpen(false);
    navigate(`/crm/leads/${result.newLeadId}`);
  };

  return (
    <div className="container">
      {/* Header */}
      <div className="card crm-lead-header-card">
        <div className="crm-lead-header-main">
          <div className="crm-lead-identity">
            <div className="crm-lead-identity-avatar" aria-hidden="true">
              {(lead.displayName || 'L').trim().charAt(0).toUpperCase()}
            </div>
            <div className="crm-lead-identity-body">
              <div className="crm-lead-title">
                {editingLeadName ? (
                  <div className="crm-inline-name-editor">
                    <input
                      type="text"
                      value={leadNameDraft}
                      maxLength={255}
                      onChange={(event) => {
                        setLeadNameDraft(event.target.value);
                        setLeadNameError('');
                      }}
                      onBlur={saveLeadName}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter') {
                          event.preventDefault();
                          event.currentTarget.blur();
                        }
                        if (event.key === 'Escape') {
                          setLeadNameDraft(lead.displayName || '');
                          setLeadNameError('');
                          setEditingLeadName(false);
                        }
                      }}
                      disabled={savingLeadName}
                      aria-label="Lead name"
                      autoFocus
                    />
                    {savingLeadName && <span>Saving…</span>}
                  </div>
                ) : (
                  <button
                    type="button"
                    className="crm-inline-name-trigger"
                    title="Click to edit Lead name"
                    onClick={() => {
                      setLeadNameDraft(lead.displayName || '');
                      setLeadNameError('');
                      setEditingLeadName(true);
                    }}
                  >
                    <h2>{lead.displayName || `Lead #${lead.id}`}</h2>
                    <span aria-hidden="true">✎</span>
                  </button>
                )}
                <span className="crm-id-chip">L-{String(lead.id).padStart(5, '0')}</span>
              </div>
              {leadNameError && <div className="crm-inline-name-error">{leadNameError}</div>}
              <div className="crm-lead-identity-caption">
                CRM lead overview and contact activity
              </div>
            </div>
          </div>
          <div className="order-actions crm-lead-header-actions">
            {primaryContact && (
              <button
                className="btn btn-success crm-lead-primary-action"
                disabled={!canCallContact(primaryContact)}
                title={contactActionTitle(primaryContact, 'call', isMobile)}
                aria-label={`Call ${primaryContact.name || 'primary contact'}`}
                onClick={() => handleCallContact(primaryContact)}
              >
                Call {primaryContact.name || 'Primary contact'}
              </button>
            )}
            {isMobile && primaryContact && !canCallContact(primaryContact) && (
              <div className="crm-muted" style={{ flexBasis: '100%', fontSize: '12px' }}>
                {contactActionTitle(primaryContact, 'call', true)}
              </div>
            )}
            <button
              className="btn btn-secondary crm-lead-whatsapp-action"
              onClick={() => setTimelineTab('whatsapp')}
            >
              WhatsApp
            </button>
            <button className="btn btn-secondary crm-lead-comment-action" onClick={() => setCommentDialogOpen(true)}>
              Add comment
            </button>
            <details ref={moreActionsRef} className="crm-lead-more-actions">
              <summary className="btn btn-secondary">More actions</summary>
              <div className="crm-lead-more-menu">
                <button
                  type="button"
                  onClick={() => {
                    closeMoreActions();
                    exportComments();
                  }}
                  disabled={exportingComments}
                >
                  {exportingComments ? 'Exporting…' : 'Export comments'}
                </button>
                {canManageLeads && (
                  <button
                    type="button"
                    onClick={() => {
                      closeMoreActions();
                      setAssignDialogOpen(true);
                    }}
                  >
                    Assign Lead
                  </button>
                )}
                {canManageLeads && (
                  <button
                    type="button"
                    onClick={() => {
                      closeMoreActions();
                      setMergeDialogOpen(true);
                    }}
                  >
                    Merge Leads
                  </button>
                )}
                {canManageLeads && (
                  <button
                    type="button"
                    onClick={() => {
                      closeMoreActions();
                      setSplitDialogOpen(true);
                    }}
                  >
                    Split Lead
                  </button>
                )}
                {canArchiveLeads && <span className="crm-lead-more-separator" />}
                {canArchiveLeads && (
                  <button
                    type="button"
                    onClick={() => {
                      closeMoreActions();
                      setArchiveAction(lead.archived ? 'restore' : 'archive');
                    }}
                  >
                    {lead.archived ? 'Unarchive Lead' : 'Archive Lead'}
                  </button>
                )}
                {canArchiveLeads && (
                  <button
                    type="button"
                    className="crm-lead-more-danger"
                    onClick={() => {
                      closeMoreActions();
                      setDeleteDialogOpen(true);
                    }}
                  >
                    Delete Lead
                  </button>
                )}
              </div>
            </details>
            <Link to="/crm/leads" className="btn btn-secondary crm-lead-back-action">← Leads</Link>
          </div>
        </div>

        <div className="crm-lead-facts" aria-label="Lead summary">
          <div className="crm-lead-fact">
            <span className="crm-lead-fact-label">Status</span>
            <span className="crm-stage-control">
              <span className={stageBadgeClass}>{STAGE_LABELS[lead.stage] || lead.stage}</span>
              {!lead.archived && !['active', 'inactive'].includes(lead.stage) && (
                <button
                  type="button"
                  className="crm-stage-edit-button"
                  onClick={() => setStageDialogOpen(true)}
                  aria-label="Edit Lead stage"
                >
                  Change
                </button>
              )}
            </span>
          </div>
          <div className="crm-lead-fact">
            <span className="crm-lead-fact-label">Owner</span>
            <strong>{lead.assignedUser?.name || 'Unassigned'}</strong>
          </div>
          <div className="crm-lead-fact">
            <span className="crm-lead-fact-label">Follow-up</span>
            <strong>{formatDate(lead.followUpDate)}</strong>
          </div>
          <div className="crm-lead-fact">
            <span className="crm-lead-fact-label">Source</span>
            <strong>{lead.createdFrom || '—'}</strong>
          </div>
        </div>

        <div className="crm-lead-associations">
          <div className="crm-lead-association-row">
            <div className="crm-lead-association-label">
              <span>Hubs</span>
              <small>{(lead.hubLinks || []).length}</small>
            </div>
            <div className="crm-lead-association-values">
              {(lead.hubLinks || []).map((link) => {
                const hubDetails = [
                  link.name || `Hub #${link.hubId}`,
                  link.city,
                  link.serviceable === true ? 'Serviceable' : '',
                  link.serviceable === false ? 'Not serviceable' : '',
                  link.distanceKm != null ? `${link.distanceKm} km away` : '',
                ].filter(Boolean).join(' · ');
                return (
                  <span
                    key={link.id}
                    className={`crm-lead-association-chip crm-lead-hub-chip${link.serviceable === false ? ' is-unserviceable' : ''}`}
                    title={hubDetails}
                  >
                    <span className="crm-lead-hub-dot" aria-hidden="true" />
                    <span>{link.name || `Hub #${link.hubId}`}</span>
                    {link.city && <small>{link.city}</small>}
                    {canManageLeads && (
                      <button
                        type="button"
                        className="crm-chip-remove"
                        onClick={() => handleRemoveHub(link)}
                        disabled={removingHubId === link.hubId}
                        aria-label={`Remove ${link.name || 'hub'}`}
                        title="Remove hub"
                      >
                        ×
                      </button>
                    )}
                  </span>
                );
              })}
              {canManageLeads && !(lead.hubLinks || []).length && lead.suggestedHub && (
                <span className="crm-hub-suggestion" title="Suggested from PMSG install district coverage">
                  <span className="crm-hub-suggestion-label">Suggested</span>
                  <span>{lead.suggestedHub.name || `#${lead.suggestedHub.hubId}`}</span>
                </span>
              )}
              {!(lead.hubLinks || []).length && !lead.suggestedHub && (
                <span className="crm-lead-empty-value">No hubs linked</span>
              )}
              {canManageLeads && (
                <button
                  type="button"
                  className="crm-add-hub-button"
                  onClick={() => setAddHubDialogOpen(true)}
                >
                  + Add hub
                </button>
              )}
            </div>
          </div>

          <div className="crm-lead-association-row">
            <div className="crm-lead-association-label">
              <span>Tags</span>
              <small>{(lead.tags || []).length}</small>
            </div>
            <div className="crm-lead-association-values">
              {(lead.tags || []).map((tag) => (
                <span key={tag} className="crm-lead-association-chip crm-lead-tag-chip">
                  {tag}
                  <button
                    type="button"
                    className="crm-chip-remove"
                    title={`Remove tag ${tag}`}
                    disabled={savingTags}
                    onClick={() => handleRemoveTag(tag)}
                  >
                    ×
                  </button>
                </span>
              ))}
              <input
                type="text"
                className="crm-tag-input"
                placeholder="+ Add a tag"
                aria-label="Add a tag"
                value={newTag}
                maxLength={40}
                disabled={savingTags}
                onChange={(event) => setNewTag(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') {
                    event.preventDefault();
                    handleAddTag();
                  }
                }}
                onBlur={handleAddTag}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Two-column layout: data left, timeline right */}
      <div className="crm-detail-grid">
        <div className="crm-detail-col">
          {pendingVideoCallRequests.length > 0 && (
            <section className="card crm-video-call-card" aria-labelledby="video-call-requests-title">
              <div className="crm-video-call-heading">
                <div>
                  <div className="crm-video-call-eyebrow">Customer request</div>
                  <h3 id="video-call-requests-title" className="section-title">Video call requested</h3>
                  <p>Track the request and mark it completed after the external video call.</p>
                </div>
                {pendingVideoCallRequests.length > 1 && (
                  <span className="crm-video-call-count">{pendingVideoCallRequests.length} pending</span>
                )}
              </div>

              <div className="crm-video-call-list">
                {pendingVideoCallRequests.map((request) => {
                  const isSaving = completingVideoCallId === request.requestId;
                  const hubLabel = request.isVirtualHub && request.parentHub
                    ? `${request.hub} · served by ${request.parentHub}`
                    : request.hub;
                  return (
                    <article className="crm-video-call-item" key={request.requestId}>
                      <div className="crm-video-call-icon" aria-hidden="true">VC</div>
                      <div className="crm-video-call-body">
                        <div className="crm-video-call-primary">
                          <strong>{formatPhone(request.callbackPhone)}</strong>
                        </div>
                        <div className="crm-video-call-meta">
                          <span>Requested {formatDateTime(request.requestedAt)}</span>
                          <span>{hubLabel || 'Hub unavailable'}</span>
                        </div>
                      </div>
                      {canCompleteVideoCalls && (
                        <label className={`crm-video-call-complete${isSaving ? ' is-saving' : ''}`}>
                          <input
                            type="checkbox"
                            checked={false}
                            disabled={Boolean(completingVideoCallId)}
                            onChange={() => completeVideoCallRequest(request.requestId)}
                          />
                          <span>{isSaving ? 'Saving…' : 'Mark completed'}</span>
                        </label>
                      )}
                    </article>
                  );
                })}
              </div>
              {videoCallError && <div className="crm-video-call-error" role="alert">{videoCallError}</div>}
            </section>
          )}

          {/* Contacts */}
          <div className="card">
            <div className="crm-contact-section-header">
              <div>
                <h3 className="section-title">Contacts</h3>
                {contacts.length > 0 && (
                  <div className="crm-contact-count">
                    {contacts.length} {contacts.length === 1 ? 'contact' : 'contacts'} linked
                  </div>
                )}
              </div>
              <button
                className="btn btn-secondary crm-btn-xs"
                onClick={() => setAddContactDialogOpen(true)}
              >
                + Add contact
              </button>
            </div>
            {contacts.length === 0 && <div className="crm-muted">No contacts on this lead.</div>}
            <div className={`crm-contact-list${showAllContacts ? ' crm-scroll' : ''}`}>
              {visibleContacts.map((c) => (
                <div
                  className={`crm-contact-row${c.isPrimary ? ' crm-contact-row-primary' : ''}`}
                  key={c.id}
                  role="button"
                  tabIndex={0}
                  title="Click to edit contact"
                  onClick={() => setEditContact(c)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      setEditContact(c);
                    }
                  }}
                >
                  <div className="crm-contact-avatar" aria-hidden="true">
                    {(c.name || 'U').trim().charAt(0).toUpperCase()}
                  </div>
                  <div className="crm-contact-info">
                    <div className="crm-contact-name-line">
                      <span className="crm-contact-name">
                        {c.name || formatPhone(c.phone) || 'Unnamed contact'}
                      </span>
                      {c.isPrimary && <span className="crm-primary-badge">Primary</span>}
                    </div>
                    <div className="crm-contact-details">
                      {c.designation && <span className="crm-contact-role">{c.designation}</span>}
                      {c.name ? (
                        <span className="crm-contact-phone">{formatPhone(c.phone) || 'Phone unavailable'}</span>
                      ) : (
                        <span className="crm-contact-phone">No name added</span>
                      )}
                    </div>
                    {(c.doNotCall || c.noWhatsapp) && (
                      <div className="crm-contact-restrictions">
                        {c.doNotCall && <span>Do not call</span>}
                        {c.noWhatsapp && <span>No WhatsApp</span>}
                      </div>
                    )}
                  </div>
                  <div
                    className="crm-contact-actions"
                    style={isMobile ? { flexWrap: 'wrap' } : undefined}
                    onClick={(event) => event.stopPropagation()}
                    onKeyDown={(event) => event.stopPropagation()}
                  >
                    <button
                      type="button"
                      className="crm-contact-edit-button"
                      onClick={() => setEditContact(c)}
                    >
                      Edit
                    </button>
                    <button
                      className="btn btn-success crm-btn-xs"
                      disabled={!canCallContact(c)}
                      title={contactActionTitle(c, 'call', isMobile)}
                      onClick={() => handleCallContact(c)}
                    >
                      Call
                    </button>
                    <button
                      className="btn btn-secondary crm-btn-xs"
                      disabled={!c.messageable}
                      title={contactActionTitle(c, 'whatsapp')}
                      onClick={() => {
                        if (!c.messageable) return;
                        setOpenWhatsappContactId(c.id);
                        setTimelineTab('whatsapp');
                      }}
                    >
                      Message
                    </button>
                    {isMobile && contactBlockedReasons(c, ['call', 'whatsapp'], true).length > 0 && (
                      <div className="crm-muted" style={{ flexBasis: '100%', fontSize: '12px' }}>
                        {contactBlockedReasons(c, ['call', 'whatsapp'], true).join(' · ')}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
            {contacts.length > 5 && (
              <button
                type="button"
                className="crm-contact-list-toggle"
                onClick={() => setShowAllContacts((current) => !current)}
              >
                {showAllContacts
                  ? 'Show fewer contacts'
                  : `View all ${contacts.length} contacts`}
              </button>
            )}

            <details className="crm-linked-identifiers">
              <summary>
                <span className="crm-linked-identifiers-title">Linked identifiers</span>
                <span className="crm-linked-identifiers-count">{externalIds.length}</span>
                <span className="crm-linked-identifiers-hint">GST, account and vendor references</span>
                <span className="crm-linked-identifiers-action crm-linked-identifiers-action-closed">
                  View identifiers
                </span>
                <span className="crm-linked-identifiers-action crm-linked-identifiers-action-open">
                  Hide identifiers
                </span>
              </summary>
              <div className="crm-linked-identifiers-body">
                <div className="crm-brand-row">
                  {externalIds.map((e) => (
                    <span key={e.id} className="crm-chip crm-linked-id">
                      <span className="crm-linked-id-type">
                        {EXTERNAL_ID_LABELS[e.type] || e.type}
                      </span>
                      <span>{externalIdDisplay(e)}</span>
                      <button
                        type="button"
                        className="crm-chip-remove"
                        onClick={() => setUnlinkExternalId(e)}
                        aria-label={`Unlink ${EXTERNAL_ID_LABELS[e.type] || e.type} ${externalIdDisplay(e)}`}
                        title="Unlink identifier"
                      >
                        ×
                      </button>
                    </span>
                  ))}
                  <button
                    type="button"
                    className="crm-chip crm-chip-link"
                    onClick={() => setLinkIdDialogOpen(true)}
                  >
                    + Link ID
                  </button>
                </div>
              </div>
            </details>
          </div>

          {/* PM Surya Ghar installs */}
          <div className="card">
            <h3 className="section-title">PM Surya Ghar installs</h3>
            {!pmsg.hasLinkedVendors ? (
              <div className="crm-placeholder">
                No PMSG vendor ID is linked to this Lead.
              </div>
            ) : !pmsg.hasCurrentData ? (
              <div className="crm-placeholder">
                No PMSG snapshot data was found for the linked vendor ID{pmsg.linkedVendorIds?.length === 1 ? '' : 's'}.
              </div>
            ) : (
              <>
                <div className="crm-brand-row" style={{ marginBottom: '12px' }}>
                  {(pmsg.vendors || []).map((vendor) => (
                    <span key={vendor.vendorId} className="crm-chip">
                      {vendor.vendorName || `Vendor ${vendor.vendorId}`} · {vendor.vendorId}
                      {vendor.snapshotDate ? ` · As of ${formatDate(vendor.snapshotDate)}` : ''}
                    </span>
                  ))}
                </div>
                <div className="crm-tiles">
                  <Tile label="National installs" value={count(pmsg.national?.installs)} sub={capacity(pmsg.national?.capacityKw)} />
                  <Tile
                    label="Last 4 weeks"
                    value={pmsg.installsLast4Weeks ? count(pmsg.installsLast4Weeks.national) : '—'}
                    sub={pmsg.installsLast4Weeks && !pmsg.installsLast4Weeks.complete ? 'Partial history' : 'National installs'}
                  />
                  <Tile label="First seen" value={formatDate(pmsg.firstSeen)} />
                  <Tile
                    label="Last seen"
                    value={formatDate(pmsg.lastSeen)}
                    sub={pmsg.hasMixedSnapshotDates
                      ? 'Linked vendors have different data dates'
                      : pmsg.snapshotDate ? `Data as of ${formatDate(pmsg.snapshotDate)}` : ''}
                  />
                </div>

                {(pmsg.states || []).length > 0 && (
                  <div style={{ marginTop: '16px' }}>
                    <div className="crm-muted" style={{ marginBottom: '6px' }}>State totals</div>
                    <div style={{ overflowX: 'auto' }}>
                    <table className="crm-mini-table">
                      <thead>
                        <tr><th>State</th><th>Installs</th><th>Capacity</th><th>Data as of</th></tr>
                      </thead>
                      <tbody>
                        {pmsg.states.map((state) => (
                          <tr key={state.state}>
                            <td>{state.state}</td>
                            <td>{count(state.installs)}</td>
                            <td>{capacity(state.capacityKw)}</td>
                            <td>{state.hasMixedSnapshotDates ? 'Mixed dates' : formatDate(state.snapshotDate)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    </div>
                  </div>
                )}

                <div style={{ marginTop: '16px' }}>
                  <div className="crm-muted" style={{ marginBottom: '6px' }}>District-wise installations</div>
                  {(pmsg.districts || []).length > 0 ? (
                    <div className="crm-scroll">
                    <table className="crm-mini-table">
                      <thead>
                        <tr><th>State</th><th>District</th><th>Installs</th><th>Capacity</th><th>Data as of</th></tr>
                      </thead>
                      <tbody>
                        {pmsg.districts.map((district) => (
                          <tr key={`${district.state}-${district.district}`}>
                            <td>{district.state}</td>
                            <td>{district.district}</td>
                            <td>{count(district.installs)}</td>
                            <td>{capacity(district.capacityKw)}</td>
                            <td>{district.hasMixedSnapshotDates ? 'Mixed dates' : formatDate(district.snapshotDate)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    </div>
                  ) : (
                    <div className="crm-placeholder">No district installation data was found.</div>
                  )}
                </div>
              </>
            )}
          </div>

          {/* Orders */}
          <div className="card">
            <h3 className="section-title">Orders</h3>
            <div className="crm-tiles">
              <Tile label="30 days" value={`${orders.count30 || 0}`} sub={money(orders.value30)} />
              <Tile label="90 days" value={`${orders.count90 || 0}`} sub={money(orders.value90)} />
              <Tile label="Lifetime" value={money(orders.lifetimeValue)} sub={`${orders.totalOrders || 0} orders`} />
              <Tile label="Avg order" value={money(orders.averageOrderValue)} />
              <Tile label="Last order" value={formatDate(orders.lastOrderDate)} />
            </div>

            <div className="crm-brand-rollup">
              <BrandRanking title="Top by value · last 90 days" rows={orders.topBrandsByValue90} metric="value" />
              <BrandRanking title="Top by units · last 90 days" rows={orders.topBrandsByUnits90} metric="units" />
              <BrandRanking title="Top by value · lifetime" rows={orders.topBrandsByValueLifetime} metric="value" />
              <BrandRanking title="Top by units · lifetime" rows={orders.topBrandsByUnitsLifetime} metric="units" />
            </div>

            {(orders.allOrders || []).length > 0 ? (
              <div style={{ marginTop: '16px' }}>
                <div className="crm-muted" style={{ marginBottom: '6px' }}>
                  Paid orders ({orders.allOrders.length})
                </div>
                <div className="crm-scroll">
                <table className="crm-mini-table">
                  <thead>
                    <tr>
                      <th>Order</th>
                      <th>Status</th>
                      <th>Value</th>
                      <th>Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {orders.allOrders.map((o) => (
                      <tr key={o.orderId}>
                        <td>
                          <Link to={`/orders/${o.orderId}`} target={isMobile ? undefined : '_blank'}>{o.orderId}</Link>
                        </td>
                        <td><span className="status-badge">{o.status}</span></td>
                        <td>{money(o.value)}</td>
                        <td>{formatDate(o.orderDate)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                </div>
              </div>
            ) : (
              <div className="crm-placeholder" style={{ marginTop: '12px' }}>No paid orders.</div>
            )}
          </div>

          {/* App & cart */}
          <div className="card">
            <h3 className="section-title">App and cart</h3>
            {(lead.appAndCart?.accounts || []).length === 0 ? (
              <div className="crm-placeholder">No OMS account is linked to this lead.</div>
            ) : (
              <div className="crm-app-accounts crm-scroll">
                {lead.appAndCart.accounts.map((account) => (
                  <div className="crm-app-account" key={account.omsUserId}>
                    <div className="crm-app-account-heading">
                      <div>
                        <div className="crm-app-account-name">
                          {account.name || `OMS account #${account.omsUserId}`}
                        </div>
                        <div className="crm-muted">OMS account #{account.omsUserId}</div>
                      </div>
                      <span className="crm-app-cart-count">
                        {account.cart?.itemCount || 0} cart line{account.cart?.itemCount === 1 ? '' : 's'}
                      </span>
                    </div>

                    <div className="crm-app-meta-grid">
                      <div className="crm-app-meta-item">
                        <div className="crm-app-meta-label">Registered</div>
                        <div className="crm-app-meta-value">{formatDate(account.registeredAt)}</div>
                      </div>
                      <div className="crm-app-meta-item">
                        <div className="crm-app-meta-label">Last login</div>
                        <div className="crm-app-meta-value">{formatDate(account.lastLoginAt)}</div>
                      </div>
                      <div className="crm-app-meta-item">
                        <div className="crm-app-meta-label">Last cart activity</div>
                        <div className="crm-app-meta-value">{formatDate(account.lastCartActivityAt)}</div>
                      </div>
                    </div>

                    <div className={`crm-current-cart ${(account.cart?.items || []).length ? 'has-items' : ''}`}>
                      <div className="crm-current-cart-heading">
                        <div>
                          <div className="crm-current-cart-title">Current cart</div>
                          <div className="crm-muted">
                            {(account.cart?.items || []).length
                              ? `${account.cart.quantity || 0} item${account.cart.quantity === 1 ? '' : 's'} across ${account.cart.itemCount || 0} line${account.cart.itemCount === 1 ? '' : 's'}`
                              : 'No items in this account’s cart'}
                          </div>
                        </div>
                        <div className="crm-current-cart-total">
                          <strong>{money(account.cart?.value)}</strong>
                          <span>Updated {formatDate(account.cart?.lastUpdatedAt)}</span>
                        </div>
                      </div>

                      {(account.cart?.items || []).length > 0 && (
                        <div className="crm-current-cart-items">
                          {account.cart.items.map((item) => (
                            <div className="crm-current-cart-item" key={item.id}>
                              <div className="crm-current-cart-product">
                                <div className="crm-current-cart-product-name">{item.name}</div>
                                <div className="crm-muted">
                                  {[item.sku, item.hub?.name].filter(Boolean).join(' · ') || 'Product details unavailable'}
                                </div>
                              </div>
                              <div className="crm-current-cart-quantity">
                                {count(item.quantity)} × {money(item.unitPrice)}
                              </div>
                              <div className="crm-current-cart-value">{money(item.value)}</div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Variables (spec §12). */}
          <div className="card">
            <h3 className="section-title">Variables</h3>
            {(lead.variables || []).length === 0 ? (
              <div className="crm-placeholder">
                No admin-defined variables yet. An admin can add them in CRM Settings.
              </div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
              <table className="crm-mini-table" style={{ width: '100%' }}>
                <tbody>
                  {lead.variables.map((variable) => (
                    <tr key={variable.id}>
                      <td style={{ width: '40%', color: '#495057' }}>
                        {variable.label}
                        <div style={{ fontSize: '11px', color: '#adb5bd' }}>{variable.key}</div>
                      </td>
                      <td>
                        {editingVariableId === variable.id ? (
                          variable.type === 'list' ? (
                            <select
                              className="form-control"
                              value={variableDraft}
                              onChange={(e) => setVariableDraft(e.target.value)}
                              disabled={savingVariable}
                            >
                              <option value="">Select a value</option>
                              {(variable.allowedValues || []).map((option) => (
                                <option key={option} value={option}>{option}</option>
                              ))}
                            </select>
                          ) : (
                            <input
                              className="form-control"
                              type={variable.type === 'date' ? 'date' : variable.type === 'number' ? 'number' : 'text'}
                              step={variable.type === 'number' ? 'any' : undefined}
                              value={variableDraft}
                              onChange={(e) => setVariableDraft(e.target.value)}
                              disabled={savingVariable}
                            />
                          )
                        ) : (
                          <span>{variable.value == null || variable.value === '' ? <span style={{ color: '#adb5bd' }}>Not set</span> : variable.value}</span>
                        )}
                      </td>
                      <td style={{ width: '1%', whiteSpace: 'nowrap', textAlign: 'right' }}>
                        {editingVariableId === variable.id ? (
                          <>
                            <button className="btn btn-primary" style={{ padding: isMobile ? '8px 14px' : '4px 10px', fontSize: isMobile ? '14px' : '12px', marginRight: '6px' }} disabled={savingVariable} onClick={() => handleSaveVariable(variable)}>
                              {savingVariable ? 'Saving…' : 'Save'}
                            </button>
                            <button className="btn btn-secondary" style={{ padding: isMobile ? '8px 14px' : '4px 10px', fontSize: isMobile ? '14px' : '12px' }} disabled={savingVariable} onClick={cancelEditVariable}>
                              Cancel
                            </button>
                          </>
                        ) : (
                          <button className="btn btn-secondary" style={{ padding: isMobile ? '8px 14px' : '4px 10px', fontSize: isMobile ? '14px' : '12px' }} onClick={() => startEditVariable(variable)}>
                            Edit
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>
            )}
          </div>
        </div>

        {/* Timeline column */}
        <div className="crm-detail-col crm-timeline-col">
          <div className="card crm-timeline-card">
            <div className="crm-timeline-heading">
              <div>
                <h3 className="section-title">Timeline</h3>
                <div className="crm-muted">Activity and communication history</div>
              </div>
              {!timelineLoading && timelinePagination.total > 0 && (
                <span className="crm-timeline-count">{timelinePagination.total} events</span>
              )}
            </div>
            <div className="crm-timeline-tabs">
              {TIMELINE_TABS.map((t) => (
                <button
                  key={t.key}
                  type="button"
                  className={`crm-timeline-tab ${timelineTab === t.key ? 'active' : ''}`}
                  aria-pressed={timelineTab === t.key}
                  onClick={() => {
                    setTimelineTab(t.key);
                    setTimelinePage(1);
                    setTimelineEvents([]);
                    setCallContactFilter('');
                  }}
                >
                  {t.label}
                </button>
              ))}
            </div>

            {timelineTab === 'call' && (() => {
              // Contacts that appear in the loaded call events, for the filter.
              const callContactsMap = new Map();
              timelineEvents.forEach((event) => {
                if (event.type !== 'call') return;
                const id = event.data?.contactId;
                if (id == null) return;
                if (!callContactsMap.has(id)) {
                  callContactsMap.set(id, event.data.contactName || `Contact ${id}`);
                }
              });
              const callContactOptions = [...callContactsMap.entries()];
              if (callContactOptions.length === 0) return null;
              return (
                <div className="crm-call-filter">
                  <span className="crm-call-filter-label">Filter by contact</span>
                  <select
                    className="form-control crm-call-filter-select"
                    value={callContactFilter}
                    onChange={(e) => setCallContactFilter(e.target.value)}
                  >
                    <option value="">All contacts</option>
                    {callContactOptions.map(([id, name]) => (
                      <option key={id} value={String(id)}>{name}</option>
                    ))}
                  </select>
                  {callContactFilter && (
                    <button
                      type="button"
                      className="crm-call-filter-clear"
                      onClick={() => setCallContactFilter('')}
                    >
                      Clear
                    </button>
                  )}
                </div>
              );
            })()}

            {timelineTab === 'whatsapp' ? (
              <CrmWhatsappConversations
                leadId={lead.id}
                hubLinks={hubLinks}
                contacts={contacts}
                openContactId={openWhatsappContactId}
                onOpenedContact={() => setOpenWhatsappContactId(null)}
              />
            ) : (
            <>
            <div className="crm-timeline-list">
              {timelineLoading && timelineEvents.length === 0 && (
                <div className="crm-muted" style={{ padding: '8px 0' }}>Loading timeline…</div>
              )}
              {timelineError && (
                <div className="crm-form-error" style={{ margin: '8px 0' }}>{timelineError}</div>
              )}
              {!timelineLoading && !timelineError && timelineEvents.length === 0 && (
                <div className="crm-muted" style={{ padding: '8px 0' }}>No events yet.</div>
              )}
              {(() => {
                // WhatsApp events are already collapsed to one-per-contact by
                // the backend. Here we only apply the optional Calls-tab
                // per-contact filter, then render.
                return timelineEvents
                  .filter((event) => {
                    if (event.type === 'call' && callContactFilter) {
                      return String(event.data?.contactId ?? '') === String(callContactFilter);
                    }
                    return true;
                  })
                  .map((event) => {
                    const d = event.data || {};
                    // One collapsed WhatsApp conversation row. Clicking opens the
                    // thread in the WhatsApp tab.
                    if (event.type === 'whatsapp') {
                      const isInbound = d.direction === 'inbound';
                      const canOpen = d.contactId != null;
                      return (
                        <div
                          className={`crm-tl-event crm-tl-event-whatsapp${canOpen ? ' crm-tl-event-clickable' : ''}`}
                          key={event.id}
                          role={canOpen ? 'button' : undefined}
                          tabIndex={canOpen ? 0 : undefined}
                          onClick={() => {
                            if (!canOpen) return;
                            setOpenWhatsappContactId(d.contactId);
                            setTimelineTab('whatsapp');
                          }}
                          onKeyDown={(e) => {
                            if (canOpen && (e.key === 'Enter' || e.key === ' ')) {
                              e.preventDefault();
                              setOpenWhatsappContactId(d.contactId);
                              setTimelineTab('whatsapp');
                            }
                          }}
                          title={canOpen ? 'Open conversation' : undefined}
                        >
                          <div className={`crm-tl-icon whatsapp${isInbound ? ' crm-tl-icon-wa-in' : ''}`}>
                            {isInbound ? '↙' : 'W'}
                          </div>
                          <div className="crm-tl-body">
                            <div className="crm-wa-line">
                              <span className="crm-wa-line-name">{d.contactName || d.contactPhone || 'Contact'}</span>
                              <span className={`crm-wa-line-dir crm-wa-line-dir-${isInbound ? 'in' : 'out'}`}>
                                {isInbound ? 'Received' : 'Sent'}
                              </span>
                              <span className="crm-wa-line-time">{formatDateTime(event.timestamp)}</span>
                            </div>
                            <div className="crm-wa-line-text">
                              {(isInbound ? '' : 'You: ') + (d.text || '—')}
                            </div>
                          </div>
                          {d.contactId != null && <span className="crm-wa-line-chevron">›</span>}
                        </div>
                      );
                    }
                    return (
                      <div className={`crm-tl-event crm-tl-event-${event.type}`} key={event.id}>
                        <div className={`crm-tl-icon ${tlIconClass(event.type)}`}>
                          {tlIconLetter(event.type)}
                        </div>
                        <div className="crm-tl-body">
                          {renderTimelineBody(event)}
                          <div className="crm-tl-meta">
                            <span>{formatDateTime(event.timestamp)}</span>
                            {d.user && <span>{d.user}</span>}
                            {d.contactName && <span>{d.contactName}</span>}
                            {d.hubNumber && <span>{d.hubNumber}</span>}
                          </div>
                        </div>
                      </div>
                    );
                  });
              })()}
            </div>

            {timelinePagination.page < timelinePagination.totalPages && (
              <div className="pagination" style={{ marginTop: '12px' }}>
                <div className="pagination-info">
                  Showing {timelineEvents.length} of {timelinePagination.total} events
                </div>
                <div className="pagination-controls">
                  <button
                    type="button"
                    className="pagination-button"
                    disabled={timelineLoading}
                    onClick={() => {
                      setTimelinePage(timelinePagination.page + 1);
                      setTimelineRevision((revision) => revision + 1);
                    }}
                  >
                    {timelineLoading ? 'Loading…' : 'Load more'}
                  </button>
                </div>
              </div>
            )}
            </>
            )}

          </div>
        </div>
      </div>

      {callContact && (
        <CrmCallDialog
          leadId={lead.id}
          contact={callContact}
          hubLinks={hubLinks}
          recentCalls={callEvents}
          onClose={() => setCallContact(null)}
          onLogged={() => {
            setCallContact(null);
            loadLead();
          }}
        />
      )}
      {dialContact && (
        <CrmDeviceDialDialog contact={dialContact} onClose={() => setDialContact(null)} />
      )}
      {commentDialogOpen && (
        <CrmCommentDialog
          leadId={lead.id}
          allowFollowUp={(lead.appAndCart?.accounts || []).length === 0}
          onClose={() => setCommentDialogOpen(false)}
          onSaved={loadLead}
        />
      )}
      {addContactDialogOpen && (
        <CrmAddContactDialog
          leadId={lead.id}
          onClose={() => setAddContactDialogOpen(false)}
          onSaved={loadLead}
        />
      )}
      {linkIdDialogOpen && (
        <CrmLinkIdDialog
          leadId={lead.id}
          onClose={() => setLinkIdDialogOpen(false)}
          onSaved={loadLead}
        />
      )}
      {mergeDialogOpen && (
        <CrmMergeLeadsDialog
          currentLead={lead}
          onClose={() => setMergeDialogOpen(false)}
          onMerged={handleMerged}
        />
      )}
      {splitDialogOpen && (
        <CrmSplitLeadDialog
          currentLead={lead}
          onClose={() => setSplitDialogOpen(false)}
          onSplit={handleSplit}
        />
      )}
      {unlinkExternalId && (
        <CrmUnlinkIdDialog
          leadId={lead.id}
          externalId={unlinkExternalId}
          onClose={() => setUnlinkExternalId(null)}
          onSaved={loadLead}
        />
      )}
      {assignDialogOpen && (
        <CrmAssignLeadDialog
          lead={lead}
          onClose={() => setAssignDialogOpen(false)}
          onAssigned={({ assignedUser }) => {
            setLead((current) => ({ ...current, assignedUser }));
            setAssignDialogOpen(false);
          }}
        />
      )}
      {archiveAction && (
        <CrmArchiveLeadDialog
          lead={lead}
          restore={archiveAction === 'restore'}
          onClose={() => setArchiveAction(null)}
          onSaved={() => {
            if (archiveAction === 'archive') {
              navigate('/crm/leads', { replace: true });
            } else {
              setArchiveAction(null);
              loadLead();
            }
          }}
        />
      )}
      {deleteDialogOpen && (
        <CrmDeleteLeadDialog
          lead={lead}
          onClose={() => setDeleteDialogOpen(false)}
          onDeleted={() => navigate('/crm/leads', { replace: true })}
        />
      )}
      {stageDialogOpen && (
        <CrmStageDialog
          lead={lead}
          onClose={() => setStageDialogOpen(false)}
          onSaved={() => {
            setStageDialogOpen(false);
            loadLead();
          }}
        />
      )}
      {editContact && (
        <CrmEditContactDialog
          leadId={lead.id}
          contact={editContact}
          onClose={() => setEditContact(null)}
          onSaved={loadLead}
        />
      )}
      {addHubDialogOpen && (
        <CrmAddHubDialog
          leadId={lead.id}
          linkedHubs={lead.hubLinks || []}
          onClose={() => setAddHubDialogOpen(false)}
          onSaved={loadLead}
        />
      )}
      {isMobile && (
        <div className="m-sticky-actions">
          {primaryContact && (
            <button
              className="btn btn-success"
              disabled={!canCallContact(primaryContact)}
              title={contactActionTitle(primaryContact, 'call', true)}
              aria-label={`Call ${primaryContact.name || 'primary contact'}`}
              onClick={() => handleCallContact(primaryContact)}
            >
              Call {primaryContact.name || 'Primary contact'}
            </button>
          )}
          <button
            className="btn btn-secondary"
            onClick={() => setTimelineTab('whatsapp')}
          >
            WhatsApp
          </button>
          {primaryContact && !canCallContact(primaryContact) && (
            <div className="crm-muted" style={{ flexBasis: '100%', fontSize: '12px' }}>
              {contactActionTitle(primaryContact, 'call', true)}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const Tile = ({ label, value, sub }) => (
  <div className="crm-tile">
    <div className="crm-tile-label">{label}</div>
    <div className="crm-tile-value">{value}</div>
    {sub != null && <div className="crm-tile-sub">{sub}</div>}
  </div>
);

const BrandRanking = ({ title, rows = [], metric }) => (
  <div className="crm-brand-ranking">
    <div className="crm-muted">{title}</div>
    {rows.length > 0 ? (
      <div className="crm-brand-row">
        {rows.map((row) => (
          <span key={row.name} className="crm-chip">
            {row.name} · {metric === 'value' ? money(row.value) : `${count(row.units)} units`}
          </span>
        ))}
      </div>
    ) : (
      <div className="crm-placeholder">No brand data</div>
    )}
  </div>
);

export default CrmLeadDetail;
